import { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { ActivityProvider } from '@/contexts/ActivityContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import ModuleGuard from '@/components/ModuleGuard';
import { Layout } from '@/components/Layout';
import { ActivityPanel } from '@/components/ActivityPanel';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from '@/pages/AuthPages';
import AuthCallback from '@/pages/AuthCallback';
import DashboardPage from '@/pages/DashboardPageNew';
import ParcellairePage from '@/pages/ParcellairePage';
import CRMPage from '@/pages/CRMPage';
import FinancePage from '@/pages/FinancePage';
import ReglementairePage from '@/pages/ReglementairePage';
import IAPage from '@/pages/IAPage';
import SettingsPage from '@/pages/SettingsPage';
import CoordonneesSocietesPage from '@/pages/CoordonneesSocietesPage';
import MesTachesPage from '@/pages/MesTachesPage';
import CASIAPage from '@/pages/CASIAPage';
import ETAPage from '@/pages/ETAPage';
import CreatorPage from '@/pages/CreatorPage';
import BrainPage from '@/pages/BrainPage';
import WorkspacePage from '@/pages/WorkspacePage';
import AgentsPage from '@/pages/AgentsPage';
import PitchDeckValentinePage from '@/pages/PitchDeckValentinePage';
import DossierCessionPage from '@/pages/DossierCessionPage';  // route accessible via URL directe pour Valentine, pas de lien menu
import ValentineMailsPage from '@/pages/ValentineMailsPage';
import AgentsConfigPage from '@/pages/AgentsConfigPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import BillingPage from '@/pages/BillingPage';
import TeamPage from '@/pages/TeamPage';
import CollaborateurDetailPage from '@/pages/CollaborateurDetailPage.jsx';
import InterventionsPage from '@/pages/InterventionsPage';
import ReferentielsPage from '@/pages/ReferentielsPage';
import ComptabilitePage from '@/pages/ComptabilitePage';
import ComptaZRRExportPage from '@/pages/ComptaZRRExportPage';
import AssistantComptablePage from '@/pages/AssistantComptablePage';
import StocksPage from '@/pages/StocksPage';
import CRMAdvancedPage from '@/pages/CRMAdvancedPage';
import MaterielPage from '@/pages/MaterielPage';
import AgentCasiaPage from '@/pages/AgentCasiaPage';
import AgentDistillateurPage from '@/pages/AgentDistillateurPage';
import BennesDistilleriePage from '@/pages/BennesDistilleriePage';
import RegistrePhytoPage from '@/pages/RegistrePhytoPage';
import VenteBureauAgricolePage from '@/pages/VenteBureauAgricolePage';
import EspaceCommercialPage from '@/pages/EspaceCommercialPage';
import CRMVenteLogicielPage from '@/pages/CRMVenteLogicielPage';
import RechercheWebPage from '@/pages/RechercheWebPage';
import ConfigurateurProspectPage from '@/pages/ConfigurateurProspectPage';
import TelegramPage from '@/pages/TelegramPage';
import BilansPage from '@/pages/BilansPage';
import MeteoPage from '@/pages/MeteoPage';
import PlanningPage from '@/pages/PlanningPage';
import AssistantVocalPage from '@/pages/AssistantVocalPage';
import AssistantTerrainPage from '@/pages/AssistantTerrainPage';
import AssistanteDirectionPage from '@/pages/AssistanteDirectionPage';
import EcommercePage from '@/pages/EcommercePage';
import ElevagePage from '@/pages/ElevagePage';
import CavePage from '@/pages/CavePage';
import AgentEleveurPage from '@/pages/AgentEleveurPage';
import RHSocialPage from '@/pages/RHSocialPage';
import CRMHubPage from '@/pages/CRMHubPage';
import GoogleIntegrationsPage from '@/pages/GoogleIntegrationsPage';
import AgentDesktopPage from '@/pages/AgentDesktopPage';
import StudioIAPage from '@/pages/StudioIAPage';
import CaloriaLandingPage from '@/pages/CaloriaLandingPage';
import SolutionsPage from '@/pages/SolutionsPage';
import SolutionMetierPage from '@/pages/SolutionMetierPage';
import VenteEtSitesPage from '@/pages/VenteEtSitesPage';
import ReunionsPage from '@/pages/ReunionsPage';
import AssistantePage from '@/pages/AssistantePage';
import AidePage from '@/pages/AidePage';
import SupportPage from '@/pages/SupportPage';
import TarifsETAPage from '@/pages/TarifsETAPage';
import BotInterventionsPage from '@/pages/BotInterventionsPage';
import JournalInterventionsPage from '@/pages/JournalInterventionsPage';
import JourneeChauffeurPage from '@/pages/JourneeChauffeurPage';
import PermissionsPage from '@/pages/PermissionsPage';
import BotSubventionsPage from '@/pages/BotSubventionsPage';
import FacturationElectroniquePage from '@/pages/FacturationElectroniquePage';
import IrrigationPage from '@/pages/IrrigationPage';
import SecurityPage from '@/pages/SecurityPage';
import ProspectionPage from '@/pages/ProspectionPage';
import EnrichirCampagnesPage from '@/pages/EnrichirCampagnesPage';
import AdminBackupsPage from '@/pages/AdminBackupsPage';
import FertilisationPage from '@/pages/FertilisationPage';
import LotsProductionPage from '@/pages/LotsProductionPage';
import CertificationsPage from '@/pages/CertificationsPage';
import AssistantsUnifiesPage from '@/pages/AssistantsUnifiesPage';
import LandingBureauAgricolePage from '@/pages/LandingBureauAgricolePage';
import StudioIAMarketingPage from '@/pages/StudioIAMarketingPage';
import ContratsETAPage from '@/pages/ContratsETAPage';
import ContratsETASimplePage from '@/pages/ContratsETASimplePage';
import ContratsIAUploadPage from '@/pages/ContratsIAUploadPage';
import ContratsUniverselsPage from '@/pages/ContratsUniverselsPage';
import SignerContratUniverselPage from '@/pages/SignerContratUniverselPage';
import SignerUniverselPage from '@/pages/SignerUniverselPage';
import SignaturesDashboardPage from '@/pages/SignaturesDashboardPage';
import SignerContratPage from '@/pages/SignerContratPage';
import ContratsAgricolesPage from '@/pages/ContratsAgricolesPage';
import CalendrierPublicationPage from '@/pages/CalendrierPublicationPage';
import ChecklistMaterielPage from '@/pages/ChecklistMaterielPage';
import ExpediteurPage from '@/pages/ExpediteurPage';
import AssistantVisioPage from '@/pages/AssistantVisioPage';
import EnregistrementAvancePage from '@/pages/EnregistrementAvancePage';
import InscriptionPubliquePage from '@/pages/InscriptionPubliquePage';
import RejoindreEquipePage from '@/pages/RejoindreEquipePage';
import LocalisationTempsReelPage from '@/pages/LocalisationTempsReelPage';
import GeofoliaImportPage from '@/pages/GeofoliaImportPage';
import AnalyseGeofoliaPage from '@/pages/AnalyseGeofoliaPage';
import PensionEquestre from '@/pages/PensionEquestre';
import ChevauxModule from '@/pages/ChevauxModule';
import DebourrageEquinPage from '@/pages/DebourrageEquinPage';
import AttelageEquinPage from '@/pages/AttelageEquinPage';
import FormationsEquinesPage from '@/pages/FormationsEquinesPage';
import BotEquinPage from '@/pages/BotEquinPage';
import AlertesPACPage from '@/pages/AlertesPACPage';
import AssurancesPage from '@/pages/AssurancesPage';
import AlertesSaisieHeuresPage from '@/pages/AlertesSaisieHeuresPage';
import VideoHubPage from '@/pages/VideoHubPage';
import VideosViralesPage from '@/pages/VideosViralesPage';
import PolitiqueConfidentialite from '@/pages/PolitiqueConfidentialite';
import GestionCommercialePage from '@/pages/GestionCommercialePage';
import SignaturePubliquePage from '@/pages/SignaturePubliquePage';
import DriveMonitorPage from '@/pages/DriveMonitorPage';
import TachesProactivesPage from '@/pages/TachesProactivesPage';
import BookingPage from '@/pages/BookingPage';
import PartenairePublicPage from '@/pages/PartenairePublicPage';
import ConseillerDashboardPage from '@/pages/ConseillerDashboardPage';
import ProspectionAutoPage from '@/pages/ProspectionAutoPage';
import SanteSystemePage from '@/pages/SanteSystemePage';
import PortailClientETALoginPage from '@/pages/PortailClientETALoginPage';
import PortailClientETAPage from '@/pages/PortailClientETAPage';
import AdminPortailETAPage from '@/pages/AdminPortailETAPage';
import DocumentPubliquePage from '@/pages/DocumentPubliquePage';
import NotificationsETAPage from '@/pages/NotificationsETAPage';
import MonSuiviGPSPage from '@/pages/MonSuiviGPSPage';
import SuiviChauffeursPage from '@/pages/SuiviChauffeursPage';
import StocksAtelierPage from '@/pages/StocksAtelierPage';
import FicheClient360Page from '@/pages/FicheClient360Page';
import CarteClientsPage from '@/pages/CarteClientsPage';
import ScanCourrierPage from '@/pages/ScanCourrierPage';
import VueGroupePage from '@/pages/VueGroupePage';
import CaveauPage from '@/pages/CaveauPage';
import EtiquetageVinPage from '@/pages/EtiquetageVinPage';
import ELabelPage from '@/pages/ELabelPage';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirects to home if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main app routes
const AppRoutes = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  // Check screen size on mount
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // CRITICAL: Détecter le session_id dans l'URL de manière synchrone (avant le render)
  // Cela évite les conditions de course avec le check d'authentification
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Landing Page Bureau Agricole - PUBLIQUE */}
      <Route
        path="/bureau-agricole"
        element={<LandingBureauAgricolePage />}
      />

      {/* Portail Client ETA - PUBLIC (login dédié) */}
      <Route
        path="/portail-eta/login"
        element={<PortailClientETALoginPage />}
      />
      <Route
        path="/portail-eta"
        element={<PortailClientETAPage />}
      />

      {/* Document public (envoyé par email, lien traçable) */}
      <Route
        path="/doc/:token"
        element={<DocumentPubliquePage />}
      />
      <Route path="/e-label/:id" element={<ELabelPage />} />

      {/* Tunnel de vente Bureau Agricole par métier - PUBLIQUE */}
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/solutions/:slug" element={<SolutionMetierPage />} />

      {/* Landing Page Caloria - PUBLIQUE */}
      <Route
        path="/caloria"
        element={<CaloriaLandingPage />}
      />

      {/* Page d'inscription publique - Tunnel de vente */}
      <Route
        path="/inscription"
        element={<InscriptionPubliquePage />}
      />

      {/* Page pour rejoindre une équipe via code d'invitation */}
      <Route
        path="/rejoindre"
        element={<RejoindreEquipePage />}
      />

      {/* Politique de Confidentialité - PUBLIQUE (requise par Meta) */}
      <Route
        path="/privacy"
        element={<PolitiqueConfidentialite />}
      />

      {/* Signature de documents - PUBLIQUE (accessible sur mobile sans compte) */}
      <Route
        path="/signature/:docId"
        element={<SignaturePubliquePage />}
      />

      {/* Booking RDV - PUBLIQUE (accessible sans compte, style Calendly) */}
      <Route
        path="/booking/:linkId"
        element={<BookingPage />}
      />

      {/* Tableau de bord Partenaire - PUBLIQUE (accessible via token unique) */}
      <Route
        path="/partenaire/:accessToken"
        element={<PartenairePublicPage />}
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conseiller"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ConseillerDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <WorkspacePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prospection-auto"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ProspectionAutoPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sante-systeme"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SanteSystemePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parcellaire"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ParcellairePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CRMAdvancedPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <FinancePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm-hub"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CRMHubPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reglementaire"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ReglementairePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <IAPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/casia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CASIAPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eta"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ETAPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contrats-eta"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ContratsETAPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contrats-eta-simple"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ContratsETASimplePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contrats-ia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ContratsIAUploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contrats-universels"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ContratsUniverselsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/signer-contrat-u/:token" element={<SignerContratUniverselPage />} />
      <Route path="/signer/:token" element={<SignerUniverselPage />} />
      <Route
        path="/signatures"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SignaturesDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/contrats/signer/:token" element={<SignerContratPage />} />
      <Route
        path="/contrats-agricoles"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ContratsAgricolesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coordonnees-societes"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CoordonneesSocietesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mes-taches"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <MesTachesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator"
        element={
          <ProtectedRoute>
            <CreatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brain"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BrainPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pitch-deck"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <PitchDeckValentinePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dossier-cession"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <DossierCessionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/valentine-mails"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ValentineMailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/config"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentsConfigPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <IntegrationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BillingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <TeamPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/:collaborateurId"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CollaborateurDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <InterventionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions/bilans"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BilansPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/registre-phyto"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <RegistrePhytoPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vente-bureau-agricole"
        element={<Navigate to="/crm-hub" replace />}
      />
      <Route
        path="/crm-vente-logiciel"
        element={<Navigate to="/crm-hub" replace />}
      />
      <Route
        path="/espace-commercial-visio"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <EspaceCommercialPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recherche-web"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <RechercheWebPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configurateur-prospect"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ConfigurateurProspectPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/telegram"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <TelegramPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meteo"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <MeteoPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <PlanningPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referentiels"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ReferentielsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comptabilite"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ComptabilitePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comptabilite/zrr-export"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ComptaZRRExportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comptabilite/assistant"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantComptablePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stocks"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <StocksPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/materiel"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <MaterielPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklist-materiel"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ChecklistMaterielPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fertilisation"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <FertilisationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lots-production"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <LotsProductionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/certifications"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CertificationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistants-ia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantsUnifiesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent-casia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentCasiaPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent-distillateur"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentDistillateurPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bennes-distillerie"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BennesDistilleriePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistant-vocal"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantVocalPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistant-terrain"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantTerrainPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistante-direction"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistanteDirectionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ecommerce"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <EcommercePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/elevage"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ElevagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/videos-virales"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <VideosViralesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vue-groupe"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <VueGroupePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/caveau"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CaveauPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/etiquetage-vin"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <EtiquetageVinPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cave"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CavePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent-eleveur"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentEleveurPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* RH / Social */}
      <Route
        path="/rh-social"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <RHSocialPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Hub Vidéo */}
      <Route
        path="/video-hub"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <VideoHubPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Alertes Saisie Heures */}
      <Route
        path="/alertes-heures"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AlertesSaisieHeuresPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Assurances */}
      <Route
        path="/assurances"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssurancesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Google Integrations - Gmail, Drive, Calendar */}
      <Route
        path="/parametres/google"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <GoogleIntegrationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Agent Desktop - Contrôle PC à distance */}
      <Route
        path="/parametres/agent-desktop"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AgentDesktopPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Studio IA & Sites - Création sites, influenceurs, marketing */}
      <Route
        path="/studio-ia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <StudioIAPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Studio IA Marketing - Création de Réels et vidéos */}
      <Route
        path="/studio-marketing"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <StudioIAMarketingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Calendrier de Publication - Réseaux sociaux */}
      <Route
        path="/calendrier-publication"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CalendrierPublicationPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Vente & Sites IA - Module unifié de vente automatisée */}
      <Route
        path="/vente-sites"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <VenteEtSitesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Réunions & Transcriptions */}
      <Route
        path="/reunions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ReunionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Assistant Visio (détection invitations, analyse enregistrements) */}
      <Route
        path="/assistant-visio"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantVisioPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Enregistrement Avancé (arrière-plan, speakers) */}
      <Route
        path="/enregistrement"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <EnregistrementAvancePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Assistante IA (fusion Assistante Direction + Réunions) */}
      <Route
        path="/assistante"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AssistantePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Aide & Guide */}
      <Route
        path="/drive-monitor"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <DriveMonitorPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/taches-ia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <TachesProactivesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Aide & Guide */}
      <Route
        path="/aide"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AidePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin — Gestion des clients Portail ETA */}
      <Route
        path="/admin/portail-eta"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AdminPortailETAPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Notifications ETA — emails client, prévention, documents traçables */}
      <Route
        path="/eta/notifications"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <NotificationsETAPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Support & Signalement de bugs */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SupportPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Tarifs ETA & Facturation */}
      <Route
        path="/tarifs-eta"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <TarifsETAPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bot-interventions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BotInterventionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/journal-interventions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <JournalInterventionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/journee-chauffeur"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <JourneeChauffeurPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/permissions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <PermissionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SecurityPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/prospection"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ProspectionPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/enrichir-campagnes"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <EnrichirCampagnesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/expediteur"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ExpediteurPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AdminBackupsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reglementaire"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ReglementairePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bot-subventions"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BotSubventionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/facturation-electronique"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <FacturationElectroniquePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/irrigation"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <IrrigationPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/geofolia"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <GeofoliaImportPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/geofolia/analyse"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AnalyseGeofoliaPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/localisation"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <LocalisationTempsReelPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pension"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <PensionEquestre />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chevaux"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ChevauxModule />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Routes Équines - Débourrage, Attelage, Formations */}
      <Route
        path="/debourrage"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <DebourrageEquinPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/attelage"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AttelageEquinPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/formations-equines"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <FormationsEquinesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bot-equin"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <BotEquinPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/alertes-pac"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <AlertesPACPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion-commerciale"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <GestionCommercialePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Mon Suivi GPS - Page mobile chauffeur (sans Layout pour expérience plein-écran) */}
      <Route
        path="/mon-suivi-gps"
        element={
          <ProtectedRoute>
            <MonSuiviGPSPage />
          </ProtectedRoute>
        }
      />

      {/* Suivi des chauffeurs - Carte temps réel admin */}
      <Route
        path="/suivi-chauffeurs"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <SuiviChauffeursPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Stocks Atelier - Phase 2 */}
      <Route
        path="/stocks-atelier"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <StocksAtelierPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fiche Client 360° (CRM enrichi) */}
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <FicheClient360Page />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Carte des Clients (Leaflet + Pappers enrichissement) */}
      <Route
        path="/carte-clients"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <CarteClientsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Scan Courrier Papier - Photo OCR IA + Drive */}
      <Route
        path="/scan-courrier"
        element={
          <ProtectedRoute>
            <Layout collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}>
              <ScanCourrierPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <PermissionsProvider>
          <ActivityProvider>
            <BrowserRouter>
              <AppRoutes />
              <ActivityPanel />
              <Toaster 
                position="top-right"
                toastOptions={{
                  style: {
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    color: 'hsl(var(--foreground))',
                  },
                }}
              />
            </BrowserRouter>
          </ActivityProvider>
        </PermissionsProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App;
